import {
  Alert, Box, Button, Chip, DialogActions, DialogContent, LinearProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ReplayIcon from '@mui/icons-material/Replay';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { DialogHeader } from '@/components/common/DialogHeader';
import type { InventoryItem, InventoryTransaction } from '@/types';
import { dateFnsLocale } from '@/utils/locale';

interface InventoryTransactionsDialogProps {
  item: InventoryItem | null;
  transactions: InventoryTransaction[];
  open: boolean;
  onClose: () => void;
  loading?: boolean;
  error?: boolean;
  onRetry: () => void;
}

const transactionTypeIcons: Record<string, React.ReactNode> = {
  purchase: <TrendingUpIcon sx={{ fontSize: 18, color: 'success.main' }} />,
  sale: <TrendingDownIcon sx={{ fontSize: 18, color: 'error.main' }} />,
  adjustment: <SwapHorizIcon sx={{ fontSize: 18, color: 'info.main' }} />,
  return: <ReplayIcon sx={{ fontSize: 18, color: 'warning.main' }} />,
};

export function InventoryTransactionsDialog({
  item, transactions, open, onClose, loading, error, onRetry,
}: InventoryTransactionsDialogProps) {
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation('common');
  const locale = dateFnsLocale();
  const formatPrice = (cents: number) => `€${(cents / 100).toFixed(2)}`;

  if (!item) return null;

  return (
    <ResponsiveDialog mobileTitle={t('transactions')} open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogHeader title={t('transactions')} icon={<HistoryIcon />} onClose={onClose} />
      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600}>{item.name}</Typography>
          <Chip label={item.partNumber} size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} />
        </Box>
        {loading ? (
          <LinearProgress aria-label={tc('loading')} />
        ) : error ? (
          <Alert severity="error" action={<Button color="inherit" onClick={onRetry}>{tc('errors.refresh')}</Button>}>
            {t('errorLoading')}
          </Alert>
        ) : transactions.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <HistoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">{t('noTransactions')}</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('date')}</TableCell>
                  <TableCell>{t('type')}</TableCell>
                  <TableCell align="right">{t('quantity')}</TableCell>
                  <TableCell align="right">{t('unitPrice')}</TableCell>
                  <TableCell>{t('stockLocations.warehouse')}</TableCell>
                  <TableCell>{t('stockLocations.bin')}</TableCell>
                  <TableCell>{t('reference')}</TableCell>
                  <TableCell>{t('createdBy')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((tx) => (
                  <TableRow key={tx.id} hover>
                    <TableCell>
                      <Typography variant="body2">
                        {format(new Date(tx.createdAt), 'd.M.yyyy', { locale })}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {transactionTypeIcons[tx.type]}
                        <Typography variant="body2">
                          {t(`transactionTypes.${tx.type}`)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        color={tx.quantity > 0 ? 'success.main' : 'error.main'}
                      >
                        {tx.quantity > 0 ? '+' : ''}
                        {tx.quantity}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {tx.unitPriceCents ? formatPrice(tx.unitPriceCents) : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>{tx.warehouseName ?? '-'}</TableCell>
                    <TableCell>{tx.binLocationCode ?? t('stockLocations.noBin')}</TableCell>
                    <TableCell>
                      {tx.workOrderNumber ? (
                        <Chip
                          label={tx.workOrderNumber}
                          size="small"
                          variant="outlined"
                          sx={{ fontFamily: 'monospace' }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {tx.notes || '-'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{tx.createdBy}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{tc('close')}</Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
